import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { useAuth } from './AuthContext';
import { useToast } from '../hooks/use-toast';

const inputCls = 'w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-[#3a9b5c] focus:ring-2 focus:ring-[#3a9b5c]/20';
const labelCls = 'mb-1.5 block text-sm font-medium text-gray-700';
const CATS = ['Plumbing', 'Electrical', 'Painting & Decorating', 'House Cleaning', 'Commercial Cleaning', 'HVAC'];

// role: 'client' | 'operative'
const SignupFlow = ({ role }) => {
  const { user, loading, register } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const isClient = role === 'client';
  const [f, setF] = useState({ name: '', email: '', phone: '', address: '', propertyType: 'Residential', location: '', trade: '', password: '', confirm: '', tc: false });
  const [submitted, setSubmitted] = useState(false);
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (loading || !user || submitted) return;
    // Already logged in (pre-existing session) → send to the relevant action
    navigate(isClient ? '/post-a-job' : '/browse-jobs');
  }, [loading, user, isClient, navigate, submitted]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 6) { toast({ title: 'Password too short', description: 'Use at least 6 characters.', variant: 'destructive' }); return; }
    if (f.password !== f.confirm) { toast({ title: 'Passwords do not match', variant: 'destructive' }); return; }
    if (!f.tc) { toast({ title: 'Please accept the Terms & Conditions', variant: 'destructive' }); return; }
    setBusy(true);
    setSubmitted(true);
    try {
      const u = await register({
        name: f.name, email: f.email, password: f.password, role,
        phone: f.phone, address: f.address, propertyType: f.propertyType,
        location: f.location, trade: f.trade, tcAccepted: true,
      });
      toast({ title: 'Account created', description: 'Welcome to EA Lead Solutions!' });
      navigate(isClient ? '/dashboard/client' : '/dashboard/operative');
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <div>
      <section className="ea-hero-gradient py-12"><div className="mx-auto max-w-3xl px-5 text-center">
        <h1 className="font-display text-3xl font-extrabold text-white sm:text-4xl">{isClient ? 'Request a Service' : 'Join Our Team'}</h1>
        <p className="mt-3 text-white/85">{isClient ? 'Create your free account to post a job and receive bids from verified London professionals.' : 'Create your professional account to start bidding on jobs across London.'}</p>
      </div></section>
      <section className="bg-[#e9f3ec] py-12"><div className="mx-auto max-w-2xl px-5">
        <form onSubmit={submit} data-testid={isClient ? 'client-signup-form' : 'operative-signup-form'} className="space-y-5 rounded-2xl bg-white p-8 shadow-md">
          <div><label className={labelCls}>Full Name *</label><input required data-testid="signup-name" className={inputCls} value={f.name} onChange={(e) => upd('name', e.target.value)} /></div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div><label className={labelCls}>Email Address *</label><input required type="email" data-testid="signup-email" className={inputCls} value={f.email} onChange={(e) => upd('email', e.target.value)} /></div>
            <div><label className={labelCls}>Phone Number *</label><input required data-testid="signup-phone" className={inputCls} value={f.phone} onChange={(e) => upd('phone', e.target.value)} /></div>
          </div>
          {isClient ? (
            <div className="grid gap-5 sm:grid-cols-2">
              <div><label className={labelCls}>Property Address *</label><input required data-testid="signup-address" className={inputCls} value={f.address} onChange={(e) => upd('address', e.target.value)} /></div>
              <div><label className={labelCls}>Property Type *</label><select data-testid="signup-property-type" className={inputCls} value={f.propertyType} onChange={(e) => upd('propertyType', e.target.value)}>{['Residential', 'Commercial', 'Industrial'].map((c) => <option key={c}>{c}</option>)}</select></div>
            </div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2">
              <div><label className={labelCls}>Location / Area *</label><input required data-testid="signup-location" className={inputCls} value={f.location} onChange={(e) => upd('location', e.target.value)} placeholder="e.g. Camden, North London" /></div>
              <div><label className={labelCls}>Trade / Service *</label><select required data-testid="signup-trade" className={inputCls} value={f.trade} onChange={(e) => upd('trade', e.target.value)}><option value="">Choose...</option>{CATS.map((c) => <option key={c}>{c}</option>)}</select></div>
            </div>
          )}
          <div className="grid gap-5 sm:grid-cols-2">
            <div><label className={labelCls}>Create Password *</label><input required type="password" minLength={6} data-testid="signup-password" className={inputCls} value={f.password} onChange={(e) => upd('password', e.target.value)} /></div>
            <div><label className={labelCls}>Confirm Password *</label><input required type="password" data-testid="signup-confirm" className={inputCls} value={f.confirm} onChange={(e) => upd('confirm', e.target.value)} /></div>
          </div>
          <label className="flex items-start gap-2 text-sm text-gray-600"><input type="checkbox" data-testid="signup-tc" checked={f.tc} onChange={(e) => upd('tc', e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#3a9b5c]" /><span>I accept the <Link to={isClient ? '/client-terms?ref=request-form' : '/freelancer-terms?ref=join-form'} target="_blank" className="font-semibold text-[#3a9b5c] underline">{isClient ? 'Client' : 'Freelancer'} Terms &amp; Conditions</Link> *</span></label>
          <button disabled={busy} data-testid="signup-submit" className="w-full rounded-full bg-[#3a9b5c] px-6 py-3.5 font-semibold text-white hover:bg-[#318150] disabled:opacity-60">{busy ? 'Creating account...' : 'Create Account'}</button>
          <p className="text-center text-sm text-gray-500">Already have an account? <Link to="/auth" className="font-semibold text-[#3a9b5c] underline">Log in</Link></p>
        </form>
      </div></section>
    </div>
  );
};

export const RequestSignup = () => <SignupFlow role="client" />;
export const JoinSignup = () => <SignupFlow role="operative" />;
export default SignupFlow;
